/**
 * Claim / reassign / unassign a repair order's responsible advisor.
 *
 * The server decides (set_ticket_advisor: front-office caller and target,
 * same shop, logical owner, audited); this hook only asks and reports. After
 * a change it re-reads the local Workboard and sends the existing shop-scoped
 * ticket-change signal so other open Workboards converge. The signal is a
 * hint to re-read; the database row is the truth.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { notifyDayboardInvalidated } from '@/utils/dayboardInvalidation';

export type AdvisorMode = 'claim' | 'reassign' | 'unassign';
export type SetAdvisorOutcome =
  | 'ASSIGNED' | 'UNCHANGED' | 'ALREADY_ASSIGNED' | 'STALE' | 'ADVISOR_NOT_ELIGIBLE' | 'TICKET_NOT_AVAILABLE';

export interface SetAdvisorArgs {
  ticketId: string;
  mode: AdvisorMode;
  advisorUserId?: string | null;
  /** The advisor the caller saw; a changed one answers STALE instead of overwriting. */
  expectedAdvisorUserId?: string | null;
}
export interface SetAdvisorResult {
  outcome: SetAdvisorOutcome;
  ticket_id: string;
  shop_id?: string;
  advisor_user_id: string | null;
}

const REFUSAL_TEXT: Partial<Record<SetAdvisorOutcome, string>> = {
  ALREADY_ASSIGNED: 'Someone else already owns this repair order.',
  STALE: 'The advisor changed while you were looking. The board has been refreshed.',
  ADVISOR_NOT_ELIGIBLE: 'That person is not a front-office member of this shop.',
  TICKET_NOT_AVAILABLE: 'This ticket is no longer available.',
};

export function useSetTicketAdvisor(shopId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: SetAdvisorArgs): Promise<SetAdvisorResult> => {
      const { data, error } = await (supabase as any).rpc('set_ticket_advisor', {
        p_ticket_id: args.ticketId,
        p_mode: args.mode,
        p_advisor_user_id: args.advisorUserId ?? null,
        p_expected_advisor_user_id: args.expectedAdvisorUserId ?? null,
      });
      if (error) throw error;
      return data as SetAdvisorResult;
    },
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['foh-custody-queue', shopId] });
      if (res.outcome === 'ASSIGNED') {
        if (shopId) {
          void notifyDayboardInvalidated({ reason: 'advisor-assignment', shopId, ticketId: res.ticket_id });
        }
        toast({ title: res.advisor_user_id ? 'Advisor assigned' : 'Advisor removed' });
      } else if (REFUSAL_TEXT[res.outcome]) {
        toast({ title: 'Not changed', description: REFUSAL_TEXT[res.outcome], variant: 'destructive' });
      }
    },
    onError: (e: Error) => {
      toast({ title: 'Advisor change refused', description: e.message, variant: 'destructive' });
    },
  });
}
